/**
 * viewport.ts — SSR-safe responsive helpers for the mobile UX layer.
 *
 * Backed by `matchMedia` with Tailwind default breakpoints:
 *   xs: <640px, sm: ≥640, md: ≥768, lg: ≥1024, xl: ≥1280.
 *
 * Defensive against SSR / non-browser environments — every hook gracefully
 * degrades to the desktop assumption (≥md) when `window` is unavailable so
 * the dashboard never crashes in a Node renderer.
 */

import { useEffect, useState } from 'react';
import { useCoarsePointer } from './device-context';
import { focusedField } from './use-visual-viewport-sheet-height';

export type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const BREAKPOINT_QUERIES: Record<Exclude<Breakpoint, 'xs'>, string> = {
  sm: '(min-width: 640px)',
  md: '(min-width: 768px)',
  lg: '(min-width: 1024px)',
  xl: '(min-width: 1280px)',
};

function hasMatchMedia(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function';
}

function computeBreakpoint(): Breakpoint {
  if (!hasMatchMedia()) return 'lg';
  if (window.matchMedia(BREAKPOINT_QUERIES.xl).matches) return 'xl';
  if (window.matchMedia(BREAKPOINT_QUERIES.lg).matches) return 'lg';
  if (window.matchMedia(BREAKPOINT_QUERIES.md).matches) return 'md';
  if (window.matchMedia(BREAKPOINT_QUERIES.sm).matches) return 'sm';
  return 'xs';
}

/**
 * Returns the currently-active Tailwind breakpoint token.
 *
 * Listens on every Tailwind breakpoint media query and re-resolves the
 * active token whenever any of them changes. Subscriptions are torn down
 * on unmount.
 */
export function useBreakpoint(): Breakpoint {
  const [bp, setBp] = useState<Breakpoint>(() => computeBreakpoint());

  useEffect(() => {
    if (!hasMatchMedia()) return;
    const mqls = Object.values(BREAKPOINT_QUERIES).map((q) => window.matchMedia(q));
    function handle() {
      setBp(computeBreakpoint());
    }
    // Re-sync once on mount in case the SSR-time initial value was 'lg'
    // but the real viewport is narrower.
    handle();
    mqls.forEach((mql) => mql.addEventListener('change', handle));
    return () => {
      mqls.forEach((mql) => mql.removeEventListener('change', handle));
    };
  }, []);

  return bp;
}

/**
 * Returns `true` when the viewport is strictly below the given breakpoint.
 * Use this in client components to decide which mobile variant to render.
 *
 * Example: `useIsBelow('md')` returns `true` for viewports <768px.
 */
export function useIsBelow(breakpoint: 'sm' | 'md' | 'lg' | 'xl'): boolean {
  const query = BREAKPOINT_QUERIES[breakpoint];
  const [isBelow, setIsBelow] = useState<boolean>(() => {
    if (!hasMatchMedia()) return false;
    return !window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (!hasMatchMedia()) return;
    const mql = window.matchMedia(query);
    function handle() {
      setIsBelow(!mql.matches);
    }
    handle();
    mql.addEventListener('change', handle);
    return () => mql.removeEventListener('change', handle);
  }, [query]);

  return isBelow;
}

/**
 * Tracks the visual viewport (iOS Safari virtual-keyboard inset). Returns
 * a `{ height, offsetTop }` pair that updates as the keyboard appears or
 * disappears. Falls back to `window.innerHeight` / `0` when the
 * `visualViewport` API is absent (older browsers, Node).
 *
 * Used by composer / sticky-footer surfaces that need to dodge the OS
 * keyboard without being occluded.
 */
export function useVisualViewport(): { height: number; offsetTop: number } {
  const [state, setState] = useState<{ height: number; offsetTop: number }>(() => {
    if (typeof window === 'undefined') return { height: 0, offsetTop: 0 };
    const vv = window.visualViewport;
    if (vv) return { height: vv.height, offsetTop: vv.offsetTop };
    return { height: window.innerHeight, offsetTop: 0 };
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const vv = window.visualViewport;
    if (!vv) {
      // No visualViewport API — fall back to window resize.
      function onResize() {
        setState({ height: window.innerHeight, offsetTop: 0 });
      }
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }
    function onChange() {
      const v = window.visualViewport;
      if (!v) return;
      setState({ height: v.height, offsetTop: v.offsetTop });
    }
    onChange();
    vv.addEventListener('resize', onChange);
    vv.addEventListener('scroll', onChange);
    return () => {
      vv.removeEventListener('resize', onChange);
      vv.removeEventListener('scroll', onChange);
    };
  }, []);

  return state;
}

/**
 * The part of the LAYOUT viewport a reader can see while typing on a touch
 * screen — `{ top, bottom }` in the coordinates `getBoundingClientRect()` uses —
 * or `null`, which means «keep the plain `0 … innerHeight` maths».
 *
 * A dropdown sized off `innerHeight` alone hangs under the keyboard on iOS,
 * which shrinks only the VISUAL viewport; the band is that viewport clipped to
 * the layout one, so it can only ever be SMALLER than what callers used before.
 *
 * `null` unless all three hold, and each one is a reason, not a precaution:
 * - a touch screen: a desktop has no software keyboard, so its dropdowns stay
 *   exactly as they were;
 * - `visualViewport` exists: without it there is nothing better to read;
 * - a text field has the focus: no field, no keyboard. That is also what keeps
 *   a stale reading out — a WKWebView once reported a keyboard minutes after it
 *   had closed, with nothing focused (see `watchKeyboardGeometry`).
 *
 * On Chrome for Android (`interactive-widget=resizes-content`) the band equals
 * `0 … innerHeight`, both having shrunk together; it is still returned, because
 * «a keyboard is up» is what lets a caller choose a different placement.
 */
export function keyboardVisibleBand(touch: boolean): { top: number; bottom: number } | null {
  if (!touch || typeof window === 'undefined') return null;
  const vv = window.visualViewport;
  if (!vv || !focusedField()) return null;
  const h = window.innerHeight;
  const top = Math.min(Math.max(0, vv.offsetTop), h);
  const bottom = Math.max(top, Math.min(h, vv.offsetTop + vv.height));
  return { top, bottom };
}

/**
 * How tall a dropdown hanging under `anchorRef` may be so that it ends above
 * the keyboard: the visible space below the anchor less `gap`, between `floor`
 * and `ceiling`. Re-measured whenever the visual viewport moves — the keyboard
 * opening AND closing — and on any scroll.
 *
 * `undefined` = no opinion (desktop, no `visualViewport`, nothing focused):
 * the caller's own CSS cap applies, i.e. exactly what it rendered before.
 */
export function useKeyboardSpaceBelow(
  anchorRef: { current: HTMLElement | null },
  open: boolean,
  { ceiling, floor = 120, gap = 12 }: { ceiling: number; floor?: number; gap?: number },
): number | undefined {
  const touch = useCoarsePointer();
  const [space, setSpace] = useState<number | undefined>(undefined);

  useEffect(() => {
    const vv = typeof window === 'undefined' ? null : window.visualViewport;
    if (!open || !touch || !vv) {
      setSpace(undefined);
      return;
    }
    function measure() {
      const band = keyboardVisibleBand(true);
      const rect = anchorRef.current?.getBoundingClientRect();
      setSpace(
        band && rect
          ? Math.min(ceiling, Math.max(floor, Math.round(band.bottom - rect.bottom - gap)))
          : undefined,
      );
    }
    measure();
    vv.addEventListener('resize', measure);
    vv.addEventListener('scroll', measure);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    document.addEventListener('focusin', measure);
    return () => {
      vv.removeEventListener('resize', measure);
      vv.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      document.removeEventListener('focusin', measure);
    };
  }, [open, touch, anchorRef, ceiling, floor, gap]);

  return space;
}
